import React, { FC, useCallback, useEffect, useState } from 'react';
import cn from 'clsx';
import Modal from 'react-modal';
import ic_ar from './polygon_prompt.png';
import {
  useDatepicker,
  START_DATE,
  END_DATE,
  FocusedInput,
} from '@datepicker-react/hooks';

import { format } from 'date-fns';
import Month from './Month';
import DatepickerContext from './context';

const MODAL_STYLE = {
  content: {
    padding: 0,
    top: '50%',
    left: 'calc(50%)',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 8px)',
    maxWidth: '1024px',
    minHeight: 'calc(274px + 25px + 72px)',
    borderRadius: '5px',
    border: '3px solid #ff1dfc',
    boxShadow: '6px 4px 0px 0px #8052ff',
  },
};

export interface DateRangePickerProps
  extends Omit<DateRangePickerState, 'focusedInput'> {
  onChange?: (state: DateRangePickerState) => void;
  focusedInput?: FocusedInput;
  title?: string;
  className?: string;
  inputCn?: string;
  dismiss?: boolean;
}

export interface DateRangePickerState {
  startDate: Date;
  endDate: Date;
  focusedInput: FocusedInput;
  neon?: string;
  nowEl?: any;
  isPledge?: boolean;
}

const DateRangePicker: FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  focusedInput = START_DATE,
  inputCn = 'shadow-r-magenta-sm',
  onChange,
  isPledge = false,
  neon = '',
  className,
  nowEl,
  dismiss = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [state, setState] = useState<DateRangePickerState>({
    startDate,
    endDate,
    focusedInput,
  });

  const handleDateChange = (data: DateRangePickerState) => {
    let payload = data;
    if (
      onChange === undefined &&
      startDate === undefined &&
      endDate === undefined
    ) {
      if (!data.focusedInput) {
        payload = { ...data, focusedInput: START_DATE };
        setState(payload);
      } else setState(data);
    }

    if (dismiss === true || !data.focusedInput) {
      hideModal();
    }

    onChange?.(payload);
  };

  useEffect(() => {
    setState({
      startDate,
      endDate,
      focusedInput,
    });
  }, [startDate, endDate, focusedInput]);

  const {
    firstDayOfWeek,
    activeMonths,
    isDateSelected,
    isDateHovered,
    isFirstOrLastSelectedDate,
    isDateBlocked,
    isDateFocused,
    focusedDate,
    onDateHover,
    onDateSelect,
    onDateFocus,
    goToPreviousMonths,
    goToNextMonths,
  } = useDatepicker({
    startDate: state.startDate,
    endDate: state.endDate,
    focusedInput: state.focusedInput,
    onDatesChange: handleDateChange,
    numberOfMonths: 1,
  });

  const showModal = useCallback(() => setOpen(true), [setOpen]);
  const hideModal = useCallback(() => setOpen(false), [setOpen]);

  return (
    <DatepickerContext.Provider
      value={
        {
          focusedDate,
          isDateFocused,
          isDateSelected,
          isDateHovered,
          isDateBlocked,
          isFirstOrLastSelectedDate,
          onDateSelect,
          onDateFocus,
          onDateHover,
        } as any
      }
    >
      <div
        className={cn('w-full grid n-col-2', className)}
        style={{ flexWrap: 'wrap' }}
      >
        <div style={{ position: 'relative' }}>
          {isPledge && (
            <div
              style={{
                padding: '8px',
                position: 'absolute',
                top: -75,
                left: 0,
                borderRadius: '8px',
                background: '#fff',
                boxShadow: '2px 2px 10px 0px rgba(0, 0, 0, 0.25)',
                width: 'max-content',
                height: 'max-content',
              }}
            >
              <div className="w-full h-full relative">
                <div className="text-left text-xs" style={{ color: 'black' }}>
                  The Pledge will commence
                  <br /> within 5 minutes of
                  <br /> the stated start time
                </div>
                <img
                  alt=""
                  style={{
                    position: 'absolute',
                    bottom: '-16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                  src={ic_ar}
                  width={14}
                  height={10}
                ></img>
              </div>
            </div>
          )}

          <h4 className="mb-2 text-lg px-4" style={{ color: neon }}>
            {isPledge ? 'Pledge' : 'Poll '} Start date
          </h4>
          <div className="tooltip-arrow" data-popper-arrow></div>
          <div className="cursor-pointer flex items-start">
            <div
              role="button"
              className="cursor-pointer flex items-start "
              onClick={() => {
                setState({ ...state, focusedInput: START_DATE });
                showModal();
              }}
              style={{ flex: 1 }}
            >
              <input
                className={
                  neon
                    ? cn(
                        'w-full px-4 py-4 bg-transparent pointer-events-none text-white'
                      )
                    : cn(
                        'w-full px-4 py-4 bg-white border-2 border-white rounded-md pointer-events-none text-purple ',
                        inputCn
                      )
                }
                style={neon ? { borderBottom: `solid 2px ${neon}` } : {}}
                value={
                  state.startDate && format(state.startDate, 'E, d MMMM y')
                }
                readOnly
              />
            </div>
            {nowEl}
          </div>
        </div>
        <div>
          <h4 className="mb-2 text-lg px-4" style={{ color: neon }}>
            {isPledge ? 'Pledge' : 'Poll '} End date
          </h4>
          <div
            role="button"
            className="cursor-pointer"
            onClick={() => {
              setState({ ...state, focusedInput: END_DATE });
              showModal();
            }}
          >
            <input
              className={
                neon
                  ? cn(
                      'w-full px-4 py-4 bg-transparent  pointer-events-none text-white'
                    )
                  : cn(
                      'w-full px-4 py-4 bg-white border-2 border-white rounded-md pointer-events-none text-purple ',
                      inputCn
                    )
              }
              style={neon ? { borderBottom: `solid 2px ${neon}` } : {}}
              value={state.endDate && format(state.endDate, 'E, d MMMM y')}
              readOnly
            />
          </div>
        </div>
      </div>

      <Modal
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-10"
        style={MODAL_STYLE}
        isOpen={open}
        onRequestClose={hideModal}
      >
        <div className="flex justify-between p-4 mx-4">
          <h2 className="text-2xl">
            Select{' '}
            <span
              className={cn({
                'text-magenta': state.focusedInput === START_DATE,
                'text-blue': state.focusedInput === END_DATE,
              })}
            >
              {state.focusedInput === END_DATE ? 'end date' : 'start date'}
            </span>
          </h2>
          <button className="underline" onClick={hideModal}>
            Close
          </button>
        </div>

        <div className="absolute flex justify-between w-full font-display">
          <button
            className="px-4 py-2 mx-2 -mt-2 bg-gray-50 hover:bg-gray-100"
            type="button"
            onClick={goToPreviousMonths}
          >
            {'<'}
          </button>
          <button
            className="px-4 py-2 mx-2 -mt-2 bg-gray-50 hover:bg-gray-100"
            type="button"
            onClick={goToNextMonths}
          >
            {'>'}
          </button>
        </div>

        <div>
          {activeMonths.map((month) => (
            <Month
              key={`${month.year}-${month.month}`}
              year={month.year}
              month={month.month}
              firstDayOfWeek={firstDayOfWeek}
            />
          ))}
        </div>
      </Modal>
    </DatepickerContext.Provider>
  );
};

export default DateRangePicker;
